import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMG } from "./shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  slide: {
    initial: { opacity: 0, x: 90 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -90 },
  },
  zoom: {
    initial: { opacity: 0, scale: 1.14 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.94 },
  },
};

export const AboutCarousel = () => {
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(0);
  const [navTick, setNavTick] = useState(0);

  useEffect(() => {
    api
      .get("/carousel")
      .then((r) => setData(r.data))
      .catch(() => setData({ images: [], interval: 5, animation: "fade" }));
  }, []);

  const images = data?.images || [];
  const intervalMs = (data?.interval || 5) * 1000;

  const goTo = (i) => {
    setIndex(((i % images.length) + images.length) % images.length);
    setNavTick((t) => t + 1);
  };

  useEffect(() => {
    if (images.length < 2) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % images.length), intervalMs);
    return () => clearInterval(t);
  }, [images.length, intervalMs, navTick]);

  if (!data) {
    return <div className="h-[280px] w-full animate-pulse rounded-[1.6rem] bg-zinc-100 sm:h-[380px]" data-testid="about-carousel-loading" />;
  }

  if (images.length === 0) {
    return (
      <div className="relative">
        <div className="absolute -bottom-4 -right-4 h-full w-full rounded-[1.6rem] border-2 border-[var(--primary)]" />
        <img src={IMG.about} alt="Técnico da Manutentudo" className="relative h-[280px] w-full rounded-[1.6rem] object-cover shadow-2xl sm:h-[380px]" />
      </div>
    );
  }

  const v = VARIANTS[data.animation] || VARIANTS.fade;
  const current = images[index % images.length];

  return (
    <div className="relative" data-testid="about-carousel">
      <div className="absolute -bottom-4 -right-4 h-full w-full rounded-[1.6rem] border-2 border-[var(--primary)]" />
      <div className="relative h-[280px] overflow-hidden rounded-[1.6rem] bg-[var(--dark-bg)] shadow-2xl sm:h-[380px]">
        <AnimatePresence mode="wait">
          <motion.img
            key={current.id}
            src={`${BACKEND}${current.url}`}
            alt={`Trabalho Manutentudo ${(index % images.length) + 1}`}
            data-testid="about-carousel-image"
            variants={v}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full object-cover"
          />
        </AnimatePresence>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
        {images.length > 1 && (
          <>
            <button
              onClick={() => goTo(index - 1)}
              data-testid="about-carousel-prev"
              aria-label="Foto anterior"
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition-[background-color,transform] duration-300 hover:scale-110 hover:bg-[var(--primary)] hover:text-black"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => goTo(index + 1)}
              data-testid="about-carousel-next"
              aria-label="Próxima foto"
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition-[background-color,transform] duration-300 hover:scale-110 hover:bg-[var(--primary)] hover:text-black"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>
      {images.length > 1 && (
        <div className="absolute -bottom-8 left-1/2 flex -translate-x-1/2 gap-2" data-testid="about-carousel-dots">
          {images.map((img, i) => (
            <button
              key={img.id}
              onClick={() => goTo(i)}
              data-testid={`about-carousel-dot-${i}`}
              aria-label={`Imagem ${i + 1}`}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                i === index % images.length ? "w-6 bg-[var(--primary)]" : "w-2 bg-zinc-300 hover:bg-zinc-400"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
